import ButtonScaleViewWithText, {
  ButtonScaleViewWithTextOpts,
} from 'src/lib/ui/components/ButtonScaleViewWithText';
import { Container } from '../../../odie';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import uiConfig from 'src/lib/ui/config';
import Match2EditorScene from 'src/game/scenes/Match2EditorScene';
import { getEditorMaxLevel, setEditorOverride } from '../../../match2.assets';
import { TextInput } from 'src/game/components/shared/TextInput';

const ui = uiConfig.buttons;

export type EditorHeaderOptions = {
  width: number;
  height: number;
  scene: Match2EditorScene;
};

export class EditorHeader extends Container {
  private flexContainer: FlexBoxView;
  private flexContainerLeft: FlexBoxView;
  private flexContainerRight: FlexBoxView;
  private saveButton!: ButtonScaleViewWithText;
  private scene: Match2EditorScene;

  constructor(options: EditorHeaderOptions) {
    super();
    this.scene = options.scene;

    this.flexContainer = new FlexBoxView({
      superview: this.view,
      width: options.width,
      height: options.height,
      flex: {
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
      },
    });

    this.flexContainerLeft = new FlexBoxView({
      superview: this.flexContainer,
      width: 300,
      height: this.flexContainer.style.height,
      flex: {
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
      },
    });

    this.flexContainerRight = new FlexBoxView({
      superview: this.flexContainer,
      width: 400,
      height: this.flexContainer.style.height,
      flex: {
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
      },
    });

    this.createLevelSelector();
    this.createControls();
  }

  private createLevelSelector() {
    const container = this.flexContainerLeft;

    new ButtonScaleViewWithText({
      ...ui.primary,
      superview: container,
      font: 'Body',
      width: 60,
      height: container.style.height,
      labelOffsetX: -5,
      labelOffsetY: -3,
      localeText: () => `<`,
      fontSize: 30,
      onClick: async () => {
        const level = Math.max(1, this.scene.level - 1);
        this.scene.editor.changeLevel(level);
      },
      tracking: {
        feature: 'editor',
        $subFeature: '<',
      },
    });

    const levelLabel = new TextInput({
      superview: container,
      width: 80,
      height: container.style.height,
      inputStyle: {
        color: '#ffffff',
        textAlign: 'center',
        fontSize: '24px',
      },
    });

    levelLabel.input.addEventListener('keydown', (event) => {
      if ((event as KeyboardEvent).key === 'Enter') {
        levelLabel.input.blur();
      }
    });

    levelLabel.input.addEventListener('blur', () => {
      const level = parseInt(levelLabel.input.value) || 1;
      if (level > getEditorMaxLevel()) {
        // Prompt user to accept auto generation, in case it goes past the current max level.
        const accept = window.confirm(
          `Editor will auto generate levels up to #${level}. Confirm?`,
        );
        if (!accept) {
          levelLabel.input.value = this.scene.level.toString();
          return;
        }
      }
      this.scene.editor.changeLevel(level);
    });

    new ButtonScaleViewWithText({
      ...ui.primary,
      superview: container,
      font: 'Body',
      width: 60,
      height: container.style.height,
      labelOffsetX: -5,
      labelOffsetY: -3,
      localeText: () => `>`,
      fontSize: 30,
      onClick: async () => {
        const level = this.scene.level + 1;
        this.scene.editor.changeLevel(level);
      },
      tracking: {
        feature: 'editor',
        $subFeature: '>',
      },
    });

    new ButtonScaleViewWithText({
      ...ui.secondary,
      superview: container,
      font: 'Body',
      width: 60,
      height: container.style.height,
      labelOffsetX: -5,
      labelOffsetY: -3,
      localeText: () => `+`,
      fontSize: 30,
      onClick: async () => {
        this.scene.editor.changeLevel(getEditorMaxLevel() + 1);
      },
      tracking: {
        feature: 'editor',
        $subFeature: '+',
      },
    });

    this.scene.onLevelChange.subscribe((level) => {
      levelLabel.input.value = level?.toString() ?? '?';
    });
  }

  private createControls() {
    const container = this.flexContainerRight;

    const defaultOpts: Partial<ButtonScaleViewWithTextOpts> = {
      superview: this.flexContainerRight,
      labelWidth: 90,
      labelHorizontalAlign: 'center',
      // labelOffsetX: -5,
      font: 'Body',
      width: 100,
      height: container.style.height,
      fontSize: 24,
    };

    this.saveButton = new ButtonScaleViewWithText({
      ...ui.primary,
      ...defaultOpts,
      localeText: () => `SAVE`,
      onClick: async () => {
        await this.scene.editor.saveInLocalStorage();
      },
      tracking: {
        feature: 'editor',
        $subFeature: 'save',
      },
    });

    new ButtonScaleViewWithText({
      ...ui.secondary,
      ...defaultOpts,
      localeText: () => `PLAY`,
      onClick: async () => {
        this.scene.editor.playCurrentLevel();
      },
      tracking: {
        feature: 'editor',
        $subFeature: 'play',
      },
    });

    new ButtonScaleViewWithText({
      ...ui.primary,
      ...defaultOpts,
      localeText: () => `CLEAR`,
      onClick: async () => {
        const level = this.scene.level;
        const ok = window.confirm(
          `Are you sure you want to clear level: ${level}?`,
        );
        if (ok) this.scene.editor.clear();
      },
      tracking: {
        feature: 'editor',
        $subFeature: 'clear',
      },
    });
  }

  public setAsSaved() {
    this.saveButton.localeText = () => `SAVED`;
    this.saveButton.label.updateOpts({
      opacity: 0.7,
    });
  }

  public setAsUnsaved() {
    this.saveButton.localeText = () => `SAVE`;
    this.saveButton.label.updateOpts({
      opacity: 1,
    });
  }
}
